import { useState, useEffect, useRef } from "react";

// ─── CONFIG ────────────────────────────────────────────────────────
const SUPABASE_URL = "https://jrswpdkngbakcdybgnvi.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Impyc3dwZGtuZ2Jha2NkeWJnbnZpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzk4NDc3MjUsImV4cCI6MjA5NTQyMzcyNX0.F-78lk7xZAsUwI1hwlsbjRrthcsF_VvEujrnbmJ0bNw";
const STRIPE_PK = "pk_test_51Tbcq0RVrQSdVo2IY23N8gWy31Ll1tJG5IUZEbfPFqNLKpvlAR6JHXpygIqZqKiUpmcft75SZwqeTmLK84ACwBm700B9rLxB0n";
const PLATFORM_FEE_PCT = 5;
const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/cNiaEZgOo6eS1Yn4t8dQQ00";

// ─── SUPABASE ──────────────────────────────────────────────────────
const sb = async (path, options = {}) => {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: options.prefer || "return=representation",
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) throw new Error(await res.text());
  const text = await res.text();
  return text ? JSON.parse(text) : [];
};

// ─── CONSTANTS ─────────────────────────────────────────────────────
const SERVICES = [
  { icon: "🔧", name: "Plumbing", color: "#1a6b9a" },
  { icon: "⚡", name: "Electrical", color: "#d4820a" },
  { icon: "🏗️", name: "General Contracting", color: "#5c3d2e" },
  { icon: "🌿", name: "Landscaping", color: "#2d6a4f" },
  { icon: "🏠", name: "Roofing", color: "#6b2d2d" },
  { icon: "❄️", name: "HVAC", color: "#1a4a6b" },
  { icon: "🎨", name: "Painting", color: "#7d3caf" },
  { icon: "🪟", name: "Windows & Doors", color: "#2c7873" },
  { icon: "🚗", name: "Auto Repair", color: "#c0392b" },
  { icon: "🧹", name: "Cleaning", color: "#1a6b5a" },
  { icon: "🔩", name: "Welding", color: "#4a4a4a" },
  { icon: "🪵", name: "Carpentry", color: "#7d5a3c" },
];
const BADGE_COLORS = { "TOP PRO": "#f59e0b", VERIFIED: "#10b981", FEATURED: "#6366f1", "RISING STAR": "#ec4899", NEW: "#6b7280" };
const SVC_COLOR = Object.fromEntries(SERVICES.map(s => [s.name, s.color]));
const SVC_ICON = Object.fromEntries(SERVICES.map(s => [s.name, s.icon]));
const initials = n => n?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";
const starStr = r => "★".repeat(Math.round(r || 5)) + "☆".repeat(5 - Math.round(r || 5));
const fmtMoney = cents => "$" + (cents / 100).toFixed(2);

// ─── STRIPE LOADER ─────────────────────────────────────────────────
let stripePromise = null;
const loadStripe = () => {
  if (stripePromise) return stripePromise;
  stripePromise = new Promise((resolve) => {
    if (window.Stripe) { resolve(window.Stripe(STRIPE_PK)); return; }
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.onload = () => resolve(window.Stripe(STRIPE_PK));
    document.head.appendChild(script);
  });
  return stripePromise;
};

// ─── INPUT COMPONENT ───────────────────────────────────────────────
const Field = ({ label, value, onChange, placeholder, type = "text", rows }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#8a7a60", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{label}</label>
    {rows ? (
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        style={{ width: "100%", padding: "11px 14px", borderRadius: 4, border: "2px solid #2a2820", background: "#0f0f0e", color: "#e8e0d0", fontFamily: "inherit", fontSize: 14, outline: "none", resize: "vertical", boxSizing: "border-box" }}
        onFocus={e => e.target.style.borderColor = "#c8860a"} onBlur={e => e.target.style.borderColor = "#2a2820"} />
    ) : (
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: "100%", padding: "11px 14px", borderRadius: 4, border: "2px solid #2a2820", background: "#0f0f0e", color: "#e8e0d0", fontFamily: "inherit", fontSize: 14, outline: "none", boxSizing: "border-box" }}
        onFocus={e => e.target.style.borderColor = "#c8860a"} onBlur={e => e.target.style.borderColor = "#2a2820"} />
    )}
  </div>
);

const Btn = ({ onClick, disabled, children, color = "#c8860a", outline = false, full = false, small = false }) => (
  <button onClick={onClick} disabled={disabled}
    style={{
      width: full ? "100%" : "auto",
      padding: small ? "8px 16px" : "12px 22px",
      background: disabled ? "#2a2820" : outline ? "transparent" : color,
      border: `2px solid ${disabled ? "#2a2820" : color}`,
      borderRadius: 4, color: disabled ? "#5a4a30" : outline ? color : "#fff",
      fontWeight: 700, fontFamily: "inherit", fontSize: small ? 12 : 14,
      cursor: disabled ? "not-allowed" : "pointer", letterSpacing: "0.03em",
    }}>
    {children}
  </button>
);

// ─── CHECKOUT MODAL ────────────────────────────────────────────────
function CheckoutModal({ pro, onClose }) {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("form"); // form | card | success | error
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const cardRef = useRef(null);

  const cents = Math.round(parseFloat(amount || "0") * 100);
  const fee = Math.round(cents * PLATFORM_FEE_PCT / 100);
  const proReceives = cents - fee;
  const color = pro.color || SVC_COLOR[pro.service] || "#c8860a";

  const proceedToCard = async () => {
    if (!amount || parseFloat(amount) < 1 || !name || !email) return;
    setStep("card");
    const s = await loadStripe();
    setStripe(s);
    setTimeout(() => {
      if (!cardRef.current) return;
      const els = s.elements({ appearance: { theme: "night", variables: { colorPrimary: "#c8860a", colorBackground: "#0f0f0e", fontFamily: "Georgia, serif" } } });
      const card = els.create("card", { style: { base: { color: "#e8e0d0", fontSize: "15px", fontFamily: "Georgia, serif", "::placeholder": { color: "#5a4a30" } } } });
      card.mount(cardRef.current);
      setElements(els);
    }, 100);
  };

  const pay = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    setErrMsg("");
    try {
      // In production: call your backend to create a PaymentIntent and get client_secret
      // Backend would use: stripe.paymentIntents.create({ amount: cents, currency: 'usd', application_fee_amount: fee, transfer_data: { destination: pro.stripe_account_id } })
      // For demo, we show the payment form works and simulate success
      const card = elements.getElement("card");
      const { error, paymentMethod } = await stripe.createPaymentMethod({ type: "card", card, billing_details: { name, email } });
      if (error) { setErrMsg(error.message); setProcessing(false); return; }
      // Simulate backend confirmation (in production, send paymentMethod.id to your server)
      await new Promise(r => setTimeout(r, 1200));
      setStep("success");
    } catch (e) {
      setErrMsg("Payment failed. Please try again.");
    }
    setProcessing(false);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#161614", border: `2px solid ${color}`, borderRadius: 12, padding: "0", maxWidth: 460, width: "100%", overflow: "hidden" }}>
        {/* Top bar */}
        <div style={{ background: color, padding: "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 700, color: "#fff", fontSize: 15 }}>
            {step === "success" ? "Payment Sent ✓" : `Book ${pro.business}`}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: "24px 24px 28px" }}>

          {/* STEP: FORM */}
          {step === "form" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, padding: "14px", background: "#0f0f0e", borderRadius: 6, border: "1px solid #2a2820" }}>
                <div style={{ width: 42, height: 42, borderRadius: 6, background: color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#fff", fontSize: 15 }}>
                  {pro.avatar || initials(pro.owner)}
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: "#f0e0c0", fontSize: 14 }}>{pro.business}</div>
                  <div style={{ fontSize: 12, color: "#6a5a40" }}>{SVC_ICON[pro.service]} {pro.service}</div>
                </div>
              </div>

              <Field label="Your Name" value={name} onChange={setName} placeholder="Full name" />
              <Field label="Email" value={email} onChange={setEmail} placeholder="you@email.com" type="email" />
              <Field label="Service Description" value={desc} onChange={setDesc} placeholder="e.g. Fix leaking kitchen pipe" rows={2} />

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#8a7a60", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Amount to Pay ($)</label>
                <input type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 150"
                  style={{ width: "100%", padding: "11px 14px", borderRadius: 4, border: "2px solid #2a2820", background: "#0f0f0e", color: "#e8e0d0", fontFamily: "inherit", fontSize: 18, fontWeight: 700, outline: "none", boxSizing: "border-box" }}
                  onFocus={e => e.target.style.borderColor = "#c8860a"} onBlur={e => e.target.style.borderColor = "#2a2820"} />
              </div>

              {cents >= 100 && (
                <div style={{ background: "#0f0f0e", border: "1px solid #2a2820", borderRadius: 6, padding: "14px 16px", marginBottom: 20, fontSize: 13 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, color: "#7a6a50" }}>
                    <span>Service total</span><span>{fmtMoney(cents)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, color: "#7a6a50" }}>
                    <span>Platform fee ({PLATFORM_FEE_PCT}%)</span><span style={{ color: "#c8860a" }}>−{fmtMoney(fee)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #2a2820", paddingTop: 8, fontWeight: 700, color: "#f0e0c0" }}>
                    <span>Pro receives</span><span style={{ color: "#10b981" }}>{fmtMoney(proReceives)}</span>
                  </div>
                </div>
              )}

              <Btn full onClick={proceedToCard} disabled={!amount || parseFloat(amount) < 1 || !name || !email}>
                Continue to Payment →
              </Btn>
            </>
          )}

          {/* STEP: CARD */}
          {step === "card" && (
            <>
              <div style={{ background: "#0f0f0e", border: "1px solid #2a2820", borderRadius: 6, padding: "14px 16px", marginBottom: 18, fontSize: 13 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, color: "#7a6a50" }}>
                  <span>Paying to</span><span style={{ color: "#f0e0c0", fontWeight: 600 }}>{pro.business}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, color: "#7a6a50" }}>
                  <span>Platform fee ({PLATFORM_FEE_PCT}%)</span><span style={{ color: "#c8860a" }}>{fmtMoney(fee)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #2a2820", paddingTop: 8, fontWeight: 800, color: "#f0e0c0", fontSize: 15 }}>
                  <span>Total charged</span><span>{fmtMoney(cents)}</span>
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#8a7a60", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Card Details</label>
                <div ref={cardRef} style={{ padding: "14px", border: "2px solid #2a2820", borderRadius: 4, background: "#0f0f0e", minHeight: 44 }} />
              </div>

              {errMsg && (
                <div style={{ background: "#3a0a0a", border: "1px solid #c0392b", borderRadius: 4, padding: "10px 14px", marginBottom: 14, color: "#ff8080", fontSize: 13 }}>
                  {errMsg}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <Btn outline onClick={() => setStep("form")}>← Back</Btn>
                <Btn full onClick={pay} disabled={processing} color="#10b981">
                  {processing ? "Processing..." : `Pay ${fmtMoney(cents)}`}
                </Btn>
              </div>

              <div style={{ marginTop: 14, textAlign: "center", fontSize: 11, color: "#4a3a28", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                🔒 Secured by Stripe · Your card info never touches our servers
              </div>
            </>
          )}

          {/* STEP: SUCCESS */}
          {step === "success" && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
              <h3 style={{ fontSize: 20, color: "#f0e0c0", margin: "0 0 10px" }}>Payment Successful!</h3>
              <p style={{ color: "#6a5a40", fontSize: 14, lineHeight: 1.7, marginBottom: 8 }}>
                <strong style={{ color: "#f0e0c0" }}>{fmtMoney(cents)}</strong> sent to <strong style={{ color: "#f0e0c0" }}>{pro.business}</strong>
              </p>
              <div style={{ background: "#0f0f0e", border: "1px solid #2a2820", borderRadius: 6, padding: "12px 16px", margin: "16px 0 24px", fontSize: 13 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#7a6a50" }}>
                  <span>Pro received</span><span style={{ color: "#10b981", fontWeight: 700 }}>{fmtMoney(proReceives)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#7a6a50" }}>
                  <span>Your platform earned</span><span style={{ color: "#c8860a", fontWeight: 700 }}>{fmtMoney(fee)}</span>
                </div>
              </div>
              <p style={{ color: "#5a4a30", fontSize: 12, marginBottom: 20 }}>A confirmation has been sent to {email}</p>
              <Btn onClick={onClose} color={color}>Done</Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ──────────────────────────────────────────────────────
export default function App() {
  const [tab, setTab] = useState("browse");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [search, setSearch] = useState("");
  const [contactModal, setContactModal] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);
  const [checkoutModal, setCheckoutModal] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ reviewer: "", rating: 5, comment: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [adForm, setAdForm] = useState({ business: "", owner: "", phone: "", tagline: "", service: "", years: "" });
  const [adSubmitting, setAdSubmitting] = useState(false);
  const [adSubmitted, setAdSubmitted] = useState(false);

  const fetchListings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await sb("listings?select=*&order=rating.desc,review_count.desc");
      setListings(data);
    } catch (e) {
      setError("Database not connected. Run setup.sql in Supabase first.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchListings(); }, []);

  const filtered = listings.filter(l => {
    const matchSvc = !selectedService || l.service === selectedService;
    const matchSearch = !search || [l.business, l.tagline, l.owner].some(f => f?.toLowerCase().includes(search.toLowerCase()));
    return matchSvc && matchSearch;
  });

  const openReviews = async (listing) => {
    setReviewModal(listing);
    setReviewForm({ reviewer: "", rating: 5, comment: "" });
    try {
      const data = await sb(`reviews?listing_id=eq.${listing.id}&order=created_at.desc`);
      setReviews(data);
    } catch { setReviews([]); }
  };

  const submitReview = async () => {
    if (!reviewForm.reviewer || !reviewForm.comment) return;
    setReviewSubmitting(true);
    try {
      await sb("reviews", { method: "POST", body: JSON.stringify({ listing_id: reviewModal.id, reviewer: reviewForm.reviewer, rating: reviewForm.rating, comment: reviewForm.comment }) });
      const all = await sb(`reviews?listing_id=eq.${reviewModal.id}`);
      const avg = all.reduce((s, r) => s + r.rating, 0) / all.length;
      await sb(`listings?id=eq.${reviewModal.id}`, { method: "PATCH", body: JSON.stringify({ rating: Math.round(avg * 10) / 10, review_count: all.length }), prefer: "return=minimal" });
      setReviews(all);
      setReviewForm({ reviewer: "", rating: 5, comment: "" });
      fetchListings();
    } catch (e) { console.error(e); }
    setReviewSubmitting(false);
  };

  const submitListing = async () => {
    if (!adForm.business || !adForm.phone || !adForm.service || !adForm.owner) return;
    setAdSubmitting(true);
    try {
      await sb("listings", { method: "POST", body: JSON.stringify({ business: adForm.business, owner: adForm.owner, phone: adForm.phone, tagline: adForm.tagline, service: adForm.service, years: parseInt(adForm.years) || 0, avatar: initials(adForm.owner), color: SVC_COLOR[adForm.service] || "#c8860a", badge: "NEW", verified: false }) });
      await fetchListings();
      setAdSubmitted(true);
    } catch (e) { console.error(e); }
    setAdSubmitting(false);
  };

  const ProCard = ({ ad }) => {
    const color = ad.color || SVC_COLOR[ad.service] || "#c8860a";
    return (
      <div style={{ background: "#161614", border: "2px solid #242420", borderRadius: 8, overflow: "hidden", transition: "border-color 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.borderColor = color}
        onMouseLeave={e => e.currentTarget.style.borderColor = "#242420"}>
        <div style={{ height: 4, background: color }} />
        <div style={{ padding: "18px 18px 16px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 46, height: 46, borderRadius: 6, background: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
              {ad.avatar || initials(ad.owner)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#f0e0c0" }}>{ad.business}</div>
                {ad.verified && <span style={{ fontSize: 12, color: "#10b981" }}>✓</span>}
              </div>
              <div style={{ fontSize: 11, color: "#6a5a40" }}>{ad.owner}{ad.years > 0 ? ` · ${ad.years} yrs` : ""}</div>
            </div>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", padding: "3px 7px", borderRadius: 3, background: `${BADGE_COLORS[ad.badge] || "#6b7280"}22`, color: BADGE_COLORS[ad.badge] || "#6b7280", border: `1px solid ${BADGE_COLORS[ad.badge] || "#6b7280"}44` }}>
              {ad.badge || "NEW"}
            </div>
          </div>

          {ad.tagline && (
            <div style={{ fontSize: 12, color: "#7a6a50", lineHeight: 1.6, borderBottom: "1px solid #2a2820", paddingBottom: 10, marginBottom: 10 }}>
              "{ad.tagline}"
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <span style={{ color: "#f59e0b", fontSize: 12 }}>{starStr(ad.rating)}</span>
              <span style={{ color: "#f0e0c0", fontWeight: 700, marginLeft: 4, fontSize: 12 }}>{(ad.rating || 5).toFixed(1)}</span>
              <span style={{ color: "#5a4a30", fontSize: 11, marginLeft: 3 }}>({ad.review_count || 0})</span>
            </div>
            <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, background: "#1e1e1c", color: "#8a7a60", border: "1px solid #2a2820" }}>
              {SVC_ICON[ad.service]} {ad.service}
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            <button onClick={() => setContactModal(ad)}
              style={{ padding: "9px 4px", background: "transparent", border: `2px solid ${color}44`, borderRadius: 4, color: color, fontWeight: 700, fontFamily: "inherit", fontSize: 11, cursor: "pointer" }}>
              📞 Call
            </button>
            <button onClick={() => openReviews(ad)}
              style={{ padding: "9px 4px", background: "transparent", border: "2px solid #2a2820", borderRadius: 4, color: "#8a7a60", fontWeight: 700, fontFamily: "inherit", fontSize: 11, cursor: "pointer" }}>
              ⭐ Reviews
            </button>
            <button onClick={() => window.open(`${STRIPE_PAYMENT_LINK}?prefilled_email=&client_reference_id=${ad.id}`, "_blank")}
              style={{ padding: "9px 4px", background: color, border: "none", borderRadius: 4, color: "#fff", fontWeight: 700, fontFamily: "inherit", fontSize: 11, cursor: "pointer" }}>
              💳 Book
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'Georgia', 'Times New Roman', serif", background: "#0f0f0e", minHeight: "100vh", color: "#e8e0d0" }}>

      {/* Header */}
      <header style={{ background: "#141412", borderBottom: "3px solid #c8860a", padding: "0 24px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 66 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 220 52" width="220" height="52">
              <defs>
                <linearGradient id="hg" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f0c040"/>
                  <stop offset="100%" stopColor="#c8680a"/>
                </linearGradient>
              </defs>
              {/* Icon box */}
              <rect x="0" y="4" width="44" height="44" rx="6" fill="#1a1a16" stroke="#c8860a" strokeWidth="1.5"/>
              {/* Hammer handle */}
              <rect x="20" y="22" width="7" height="22" rx="3" fill="#7a6040" transform="rotate(-35 23 33)"/>
              {/* Hammer head */}
              <rect x="8" y="8" width="28" height="16" rx="4" fill="url(#hg)"/>
              {/* Wrench */}
              <g transform="translate(28,10) rotate(40)">
                <rect x="-3" y="-12" width="6" height="20" rx="3" fill="#8a7050" opacity="0.9"/>
                <ellipse cx="0" cy="-12" rx="6" ry="4" fill="none" stroke="#8a7050" strokeWidth="3"/>
              </g>
              {/* TRADE text */}
              <text x="54" y="28" fontFamily="Georgia, serif" fontSize="22" fontWeight="700" letterSpacing="1" fill="#f5e6c8">TRADE</text>
              {/* HUB text in gold */}
              <text x="54" y="47" fontFamily="Georgia, serif" fontSize="22" fontWeight="700" letterSpacing="3" fill="url(#hg)">HUB</text>
              {/* Tagline */}
              <text x="140" y="28" fontFamily="Georgia, serif" fontSize="8" letterSpacing="1.5" fill="#6a5a40" fontStyle="italic">Blue Collar</text>
              <text x="140" y="40" fontFamily="Georgia, serif" fontSize="8" letterSpacing="1.5" fill="#6a5a40" fontStyle="italic">Marketplace</text>
              {/* Gold divider */}
              <line x1="133" y1="10" x2="133" y2="44" stroke="#c8860a" strokeWidth="1" opacity="0.4"/>
            </svg>
          </div>
          <nav style={{ display: "flex", gap: 4 }}>
            {[{ id: "browse", label: "Browse" }, { id: "pros", label: "Find Pros" }, { id: "advertise", label: "List Business" }].map(t => (
              <button key={t.id} onClick={() => { setTab(t.id); setSelectedService(null); }}
                style={{ padding: "7px 14px", border: "none", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: tab === t.id ? "#c8860a" : "transparent", color: tab === t.id ? "#0f0f0e" : "#8a7a60" }}>
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "36px 24px" }}>

        {/* BROWSE */}
        {tab === "browse" && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div style={{ display: "inline-block", background: "#c8860a22", color: "#c8860a", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", padding: "4px 14px", border: "1px solid #c8860a44", borderRadius: 3, marginBottom: 18, textTransform: "uppercase" }}>
                Skilled Trades & Services
              </div>
              <h1 style={{ fontSize: "clamp(26px,4vw,46px)", fontWeight: 700, color: "#f5e6c8", margin: "0 0 12px", lineHeight: 1.15 }}>
                Find Trusted Tradespeople<br /><span style={{ color: "#c8860a" }}>In Your Community</span>
              </h1>
              <p style={{ fontSize: 15, color: "#6a5a40", maxWidth: 440, margin: "0 auto 28px", lineHeight: 1.7 }}>
                Book and pay local professionals securely. We take just 5% — you keep 95%.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", maxWidth: 480, margin: "0 auto" }}>
                <input placeholder="Search services or businesses..." value={search} onChange={e => setSearch(e.target.value)}
                  style={{ flex: 1, padding: "12px 16px", borderRadius: 4, border: "2px solid #2a2820", background: "#1a1a18", color: "#e8e0d0", fontFamily: "inherit", fontSize: 14, outline: "none" }} />
                <button onClick={() => setTab("pros")} style={{ padding: "12px 22px", background: "#c8860a", border: "none", borderRadius: 4, color: "#0f0f0e", fontWeight: 700, fontFamily: "inherit", fontSize: 14, cursor: "pointer" }}>Search</button>
              </div>
            </div>

            {/* Stats bar */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 40 }}>
              {[
                { label: "Active Pros", value: listings.length, icon: "👷" },
                { label: "Platform Fee", value: "5%", icon: "💰" },
                { label: "Stripe Secured", value: "256-bit SSL", icon: "🔒" },
              ].map(s => (
                <div key={s.label} style={{ background: "#161614", border: "1px solid #242420", borderRadius: 6, padding: "16px", textAlign: "center" }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#c8860a", marginBottom: 2 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: "#5a4a30", textTransform: "uppercase", letterSpacing: "0.08em" }}>{s.label}</div>
                </div>
              ))}
            </div>

            <h2 style={{ fontSize: 18, fontWeight: 700, color: "#f5e6c8", margin: "0 0 18px" }}>All Service Categories</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 12 }}>
              {SERVICES.map(svc => (
                <div key={svc.name} onClick={() => { setSelectedService(svc.name); setTab("pros"); }}
                  style={{ background: "#161614", border: "2px solid #242420", borderRadius: 6, padding: "18px 16px", cursor: "pointer", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = svc.color; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#242420"; e.currentTarget.style.transform = "none"; }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{svc.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#f0e0c0", marginBottom: 4 }}>{svc.name}</div>
                  <div style={{ fontSize: 11, color: svc.color, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    {listings.filter(l => l.service === svc.name).length} pros →
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FIND PROS */}
        {tab === "pros" && (
          <div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
              <button onClick={() => setSelectedService(null)}
                style={{ padding: "6px 12px", borderRadius: 4, border: `2px solid ${!selectedService ? "#c8860a" : "#2a2820"}`, background: !selectedService ? "#c8860a" : "transparent", color: !selectedService ? "#0f0f0e" : "#8a7a60", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer", textTransform: "uppercase" }}>
                All
              </button>
              {SERVICES.map(svc => (
                <button key={svc.name} onClick={() => setSelectedService(svc.name)}
                  style={{ padding: "6px 10px", borderRadius: 4, border: `2px solid ${selectedService === svc.name ? svc.color : "#2a2820"}`, background: selectedService === svc.name ? svc.color + "22" : "transparent", color: selectedService === svc.name ? svc.color : "#8a7a60", fontFamily: "inherit", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                  {svc.icon} {svc.name}
                </button>
              ))}
            </div>

            <input placeholder="Search by name, tagline, owner..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", padding: "10px 14px", borderRadius: 4, border: "2px solid #2a2820", background: "#161614", color: "#e8e0d0", fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 20 }} />

            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 18 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "#f5e6c8", margin: 0 }}>{selectedService || "All Professionals"}</h2>
              <span style={{ color: "#5a4a30", fontSize: 12 }}>{loading ? "Loading..." : `${filtered.length} listed`}</span>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#5a4a30" }}>Loading from database...</div>
            ) : error ? (
              <div style={{ textAlign: "center", padding: "40px", border: "2px solid #3a1a00", borderRadius: 8, background: "#1a0e00" }}>
                <div style={{ color: "#c8860a", fontWeight: 700, marginBottom: 6 }}>⚠ Database not connected</div>
                <div style={{ color: "#6a5a40", fontSize: 13 }}>{error}</div>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", border: "2px dashed #2a2820", borderRadius: 8, color: "#5a4a30" }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>🔍</div>
                No pros found — try a different filter or list yours!
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
                {filtered.map(ad => <ProCard key={ad.id} ad={ad} />)}
              </div>
            )}

            <div style={{ marginTop: 36, padding: "24px", background: "#161614", border: "2px dashed #3a3020", borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#f0e0c0", marginBottom: 6 }}>🛠️ Are you a tradesperson?</div>
              <div style={{ fontSize: 12, color: "#6a5a40", marginBottom: 14 }}>List free, get paid through the app, keep 95% of every job.</div>
              <Btn onClick={() => setTab("advertise")}>List My Business</Btn>
            </div>
          </div>
        )}

        {/* ADVERTISE */}
        {tab === "advertise" && (
          <div style={{ maxWidth: 580, margin: "0 auto" }}>
            {adSubmitted ? (
              <div style={{ textAlign: "center", padding: "52px 28px", background: "#161614", border: "2px solid #2d6a4f", borderRadius: 8 }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>✅</div>
                <h2 style={{ fontSize: 20, color: "#f0e0c0", marginBottom: 8 }}>You're Live!</h2>
                <p style={{ color: "#6a5a40", fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
                  <strong style={{ color: "#f0e0c0" }}>{adForm.business}</strong> is now live. Customers can find and pay you directly through TradeHub.
                </p>
                <Btn onClick={() => { setAdSubmitted(false); setAdForm({ business: "", owner: "", phone: "", tagline: "", service: "", years: "" }); setTab("pros"); }}>
                  View All Pros
                </Btn>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: "inline-block", background: "#c8860a22", color: "#c8860a", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", padding: "4px 12px", border: "1px solid #c8860a44", borderRadius: 3, marginBottom: 12, textTransform: "uppercase" }}>Free to List</div>
                  <h2 style={{ fontSize: 24, fontWeight: 700, color: "#f5e6c8", margin: "0 0 8px" }}>List Your Trade Business</h2>
                  <p style={{ color: "#6a5a40", fontSize: 13, lineHeight: 1.7 }}>Customers book and pay you through the app. You keep 95% of every job — TradeHub takes just 5%.</p>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 24 }}>
                  {[{ icon: "💳", t: "Get Paid Online", d: "Stripe-secured payments" }, { icon: "📍", t: "Local Discovery", d: "Show up in searches" }, { icon: "⭐", t: "Build Reviews", d: "Earn trust over time" }].map(p => (
                    <div key={p.t} style={{ background: "#161614", border: "1px solid #242420", borderRadius: 6, padding: "12px 10px", textAlign: "center" }}>
                      <div style={{ fontSize: 20, marginBottom: 5 }}>{p.icon}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#c8860a", marginBottom: 3 }}>{p.t}</div>
                      <div style={{ fontSize: 10, color: "#5a4a30" }}>{p.d}</div>
                    </div>
                  ))}
                </div>

                <div style={{ background: "#161614", border: "2px solid #242420", borderRadius: 8, padding: "24px" }}>
                  <Field label="Business Name *" value={adForm.business} onChange={v => setAdForm(f => ({ ...f, business: v }))} placeholder="e.g. Mike's Pro Plumbing" />
                  <Field label="Your Name *" value={adForm.owner} onChange={v => setAdForm(f => ({ ...f, owner: v }))} placeholder="e.g. Mike Torrez" />
                  <Field label="Phone Number *" value={adForm.phone} onChange={v => setAdForm(f => ({ ...f, phone: v }))} placeholder="(555) 000-0000" />
                  <Field label="Years in Business" value={adForm.years} onChange={v => setAdForm(f => ({ ...f, years: v }))} placeholder="e.g. 10" type="number" />
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#8a7a60", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Service Category *</label>
                    <select value={adForm.service} onChange={e => setAdForm(f => ({ ...f, service: e.target.value }))}
                      style={{ width: "100%", padding: "11px 14px", borderRadius: 4, border: "2px solid #2a2820", background: "#0f0f0e", color: adForm.service ? "#e8e0d0" : "#5a4a30", fontFamily: "inherit", fontSize: 14, outline: "none" }}>
                      <option value="">Select your trade...</option>
                      {SERVICES.map(s => <option key={s.name} value={s.name}>{s.icon} {s.name}</option>)}
                    </select>
                  </div>
                  <Field label="Tagline / Description" value={adForm.tagline} onChange={v => setAdForm(f => ({ ...f, tagline: v }))} placeholder="e.g. 24/7 emergency service. Licensed & insured." rows={2} />
                  <Btn full onClick={submitListing} disabled={adSubmitting || !adForm.business || !adForm.phone || !adForm.service || !adForm.owner}>
                    {adSubmitting ? "Saving..." : "List My Business — Free"}
                  </Btn>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* CONTACT MODAL */}
      {contactModal && (
        <div onClick={() => setContactModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#161614", border: `2px solid ${contactModal.color || "#c8860a"}`, borderRadius: 10, padding: "24px", maxWidth: 360, width: "100%" }}>
            <div style={{ height: 4, background: contactModal.color || "#c8860a", margin: "-24px -24px 20px", borderRadius: "8px 8px 0 0" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 46, height: 46, borderRadius: 6, background: contactModal.color || "#c8860a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#fff" }}>
                {contactModal.avatar || initials(contactModal.owner)}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#f0e0c0" }}>{contactModal.business}</div>
                <div style={{ fontSize: 12, color: "#6a5a40" }}>{contactModal.owner}</div>
              </div>
            </div>
            <div style={{ background: "#0f0f0e", border: "1px solid #2a2820", borderRadius: 6, padding: "14px", marginBottom: 14, textAlign: "center" }}>
              <div style={{ fontSize: 10, color: "#5a4a30", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>Phone</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#c8860a" }}>{contactModal.phone}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn full outline onClick={() => { window.open(`${STRIPE_PAYMENT_LINK}?client_reference_id=${contactModal.id}`, "_blank"); setContactModal(null); }} color={contactModal.color || "#c8860a"}>💳 Book & Pay</Btn>
              <Btn full onClick={() => setContactModal(null)}>Close</Btn>
            </div>
          </div>
        </div>
      )}

      {/* REVIEWS MODAL */}
      {reviewModal && (
        <div onClick={() => setReviewModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#161614", border: "2px solid #2a2820", borderRadius: 10, padding: "24px", maxWidth: 480, width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#f0e0c0" }}>{reviewModal.business}</div>
                <div style={{ fontSize: 11, color: "#6a5a40" }}>{reviews.length} reviews</div>
              </div>
              <button onClick={() => setReviewModal(null)} style={{ background: "none", border: "none", color: "#6a5a40", fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ background: "#0f0f0e", border: "1px solid #2a2820", borderRadius: 6, padding: "16px", marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#c8860a", marginBottom: 10 }}>Leave a Review</div>
              <Field label="Your Name" value={reviewForm.reviewer} onChange={v => setReviewForm(f => ({ ...f, reviewer: v }))} placeholder="Your name" />
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#8a7a60", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Rating</label>
                <div style={{ display: "flex", gap: 4 }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => setReviewForm(f => ({ ...f, rating: n }))}
                      style={{ fontSize: 22, background: "none", border: "none", cursor: "pointer", color: n <= reviewForm.rating ? "#f59e0b" : "#3a3020", padding: 2 }}>★</button>
                  ))}
                </div>
              </div>
              <Field label="Comment" value={reviewForm.comment} onChange={v => setReviewForm(f => ({ ...f, comment: v }))} placeholder="Share your experience..." rows={2} />
              <Btn full onClick={submitReview} disabled={reviewSubmitting || !reviewForm.reviewer || !reviewForm.comment} color="#10b981">
                {reviewSubmitting ? "Submitting..." : "Submit Review"}
              </Btn>
            </div>
            {reviews.length === 0 ? (
              <div style={{ textAlign: "center", padding: "20px", color: "#5a4a30", fontSize: 13 }}>No reviews yet. Be the first!</div>
            ) : reviews.map(r => (
              <div key={r.id} style={{ borderBottom: "1px solid #1e1e1c", paddingBottom: 12, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, color: "#f0e0c0", fontSize: 13 }}>{r.reviewer}</div>
                  <span style={{ color: "#f59e0b", fontSize: 12 }}>{starStr(r.rating)}</span>
                </div>
                <div style={{ fontSize: 12, color: "#7a6a50", lineHeight: 1.6 }}>{r.comment}</div>
                <div style={{ fontSize: 10, color: "#3a3020", marginTop: 4 }}>{new Date(r.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CHECKOUT MODAL removed — using Stripe Payment Link */}

      <footer style={{ borderTop: "1px solid #1e1e1c", padding: "18px 24px", textAlign: "center", color: "#3a3020", fontSize: 11, marginTop: 60 }}>
        © 2026 TradeHub · 5% Platform Fee · Payments by Stripe · Database by Supabase
      </footer>
    </div>
  );
}
